/**
 * CreateManagedSqlDbModal Component
 *
 * Modal for creating a new managed SQL database.
 */

import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Textarea } from '../../../components/ui/Textarea';
import { Checkbox } from '../../../components/ui/Checkbox';
import { Alert } from '../../../components/ui/Alert';
import { FormField } from '../../../components/forms/FormField';
import { useCreateManagedSqlDb } from '../hooks/useCreateManagedSqlDb';
import {
  createManagedSqlDbFormSchema,
  type CreateManagedSqlDbFormData,
} from '../../../lib/validators/managed-sql-db';

/**
 * Generate database code from name
 * Converts "Production Database" to "production-database"
 */
const generateCodeFromName = (name: string): string => {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/^[^a-z]+/, '') // Ensure starts with letter
    .slice(0, 30);
};

interface CreateManagedSqlDbModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
}

/**
 * CreateManagedSqlDbModal component
 */
export const CreateManagedSqlDbModal: React.FC<CreateManagedSqlDbModalProps> = ({
  isOpen,
  onClose,
  tenantId,
}) => {
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [autoGenerateCode, setAutoGenerateCode] = useState<boolean>(true);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<CreateManagedSqlDbFormData>({
    resolver: zodResolver(createManagedSqlDbFormSchema),
    defaultValues: {
      managedSqlDbCode: '',
      managedSqlDbName: '',
      description: '',
    },
  });

  const watchName = watch('managedSqlDbName');

  // Auto-generate database code from name
  useEffect(() => {
    if (autoGenerateCode && watchName) {
      const generatedCode = generateCodeFromName(watchName);
      setValue('managedSqlDbCode', generatedCode);
    }
  }, [watchName, autoGenerateCode, setValue]);

  const { mutate: createManagedSqlDb, isPending } = useCreateManagedSqlDb(tenantId, {
    onSuccess: () => {
      setSuccessMessage('Database creation initiated. It may take a moment to become available.');
      setErrorMessage('');
      setTimeout(() => {
        reset();
        onClose();
        setSuccessMessage('');
      }, 1500);
    },
    onError: (error) => {
      setErrorMessage(error);
      setSuccessMessage('');
    },
  });

  const onSubmit = (data: CreateManagedSqlDbFormData) => {
    setErrorMessage('');
    setSuccessMessage('');

    const dbData = {
      managedSqlDbCode: data.managedSqlDbCode,
      managedSqlDbName: data.managedSqlDbName,
      description: data.description || undefined,
    };

    createManagedSqlDb(dbData);
  };

  const handleClose = () => {
    if (!isPending) {
      reset();
      setErrorMessage('');
      setSuccessMessage('');
      setAutoGenerateCode(true);
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <Modal.Header>Create Managed SQL Database</Modal.Header>
      <Modal.Body>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Info Note */}
          <Alert variant="info">
            Creates a PostgreSQL database in the shared Aurora cluster. Applications and ELT jobs
            can be granted access after creation.
          </Alert>

          {/* Error Message */}
          {errorMessage && <Alert variant="error">{errorMessage}</Alert>}

          {/* Success Message */}
          {successMessage && <Alert variant="success">{successMessage}</Alert>}

          {/* Database Name */}
          <FormField label="Database Name" required error={errors.managedSqlDbName?.message}>
            <Input
              {...register('managedSqlDbName')}
              placeholder="e.g., Production Database"
              error={!!errors.managedSqlDbName}
            />
          </FormField>

          {/* Database Code */}
          <FormField label="Database Code" required error={errors.managedSqlDbCode?.message}>
            <Input
              {...register('managedSqlDbCode')}
              placeholder="e.g., prod"
              error={!!errors.managedSqlDbCode}
              disabled={autoGenerateCode}
            />
            <div className="mt-2 flex items-center">
              <Checkbox
                id="autoGenerateManagedSqlDbCode"
                checked={autoGenerateCode}
                onChange={(e) => setAutoGenerateCode(e.target.checked)}
              />
              <label htmlFor="autoGenerateManagedSqlDbCode" className="ml-2 text-sm text-gray-600">
                Auto-generate code from name
              </label>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Code is immutable after creation. Use 2-30 lowercase letters, numbers, and hyphens.
              Must start with a letter.
            </p>
          </FormField>

          {/* Description */}
          <FormField label="Description" error={errors.description?.message}>
            <Textarea
              {...register('description')}
              placeholder="Describe the purpose of this database..."
              rows={3}
              error={!!errors.description}
            />
          </FormField>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={handleClose} disabled={isPending}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={isPending}>
              Create Database
            </Button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};
